/**
 * Cron Timezone Converter
 *
 * A generic implementation for converting cron expressions between
 * UTC and any local timezone with high accuracy.
 */

export class CronTimezoneConverter {
    /**
     * Converts a UTC cron expression to local timezone
     * @param cron The UTC cron expression to convert
     * @returns The cron expression in local timezone
     */
    public convertFromUTC(cron: string, offsetMinutes: number): string {
        if (!cron || cron.trim() === '') {
            return cron;
        }

        // For any timezone, use the generic algorithm
        // Negate the offset because getTimezoneOffset() returns the opposite of what we need
        return this.convertWithOffset(cron, -offsetMinutes);
    }

    /**
     * Converts a local timezone cron expression to UTC
     * @param cron The local cron expression to convert
     * @returns The cron expression in UTC
     */
    public convertToUTC(cron: string, offsetMinutes: number): string {
        if (!cron || cron.trim() === '') {
            return cron;
        }

        // For any timezone, use the generic algorithm
        return this.convertWithOffset(cron, offsetMinutes);
    }

    /**
     * Generic method to convert cron expressions with a timezone offset
     * @param cron The cron expression to convert
     * @param offsetMinutes The timezone offset in minutes to apply
     * @returns The converted cron expression
     */
    private convertWithOffset(cron: string, offsetMinutes: number): string {
        const cronParts = cron.split(' ');
        if (cronParts.length < 5) {
            console.error('Invalid cron expression format');
            return cron;
        }

        const hasSeconds = cronParts.length >= 6;
        const minuteIndex = hasSeconds ? 1 : 0;
        const hourIndex = hasSeconds ? 2 : 1;
        const dayOfMonthIndex = hasSeconds ? 3 : 2;
        const monthIndex = hasSeconds ? 4 : 3;
        const dayOfWeekIndex = hasSeconds ? 5 : 4;

        // Check for common recurring patterns that should preserve their semantic meaning

        // If hour is * or ? or represents any recurring hourly pattern (e.g., 0/1, 0/2, etc.), return as is
        // This preserves the semantic meaning of "every X hours" regardless of timezone
        if (
            cronParts[hourIndex] === '*' ||
            cronParts[hourIndex] === '?' ||
            (cronParts[hourIndex].includes('/') && cronParts[hourIndex].startsWith('0/'))
        ) {
            return cron;
        }

        // Check for daily, weekly, monthly, yearly patterns
        // Daily pattern: typically has specific hour but * for day-of-month and month
        const isDailyPattern = cronParts[dayOfMonthIndex] === '*' && cronParts[monthIndex] === '*';

        // Weekly pattern: typically has specific day-of-week but ? for day-of-month
        const isWeeklyPattern =
            cronParts[dayOfMonthIndex] === '?' && cronParts[dayOfWeekIndex] !== '*' && cronParts[dayOfWeekIndex] !== '?';

        // Monthly pattern: typically has specific day-of-month but * for month
        const isMonthlyPattern = /^\d+$/.test(cronParts[dayOfMonthIndex]) && cronParts[monthIndex] === '*';

        // Yearly pattern: typically has specific day-of-month and specific month
        const isYearlyPattern = /^\d+$/.test(cronParts[dayOfMonthIndex]) && /^\d+$/.test(cronParts[monthIndex]);

        // For these patterns, we should only adjust the hour and minute, but preserve the pattern type
        if (isDailyPattern || isWeeklyPattern || isMonthlyPattern || isYearlyPattern) {
            // We'll continue with the conversion but make sure not to change the pattern type
            // The code below will handle the hour and minute adjustments properly
        }

        // Check if we can adjust days (if day of month or day of week is not a pattern)
        const canAdjustDays = !(
            (cronParts[dayOfMonthIndex] === '*' ||
                cronParts[dayOfMonthIndex] === '?' ||
                cronParts[dayOfMonthIndex].includes(',') ||
                cronParts[dayOfMonthIndex].includes('/')) &&
            (cronParts[dayOfWeekIndex] === '*' ||
                cronParts[dayOfWeekIndex] === '?' ||
                cronParts[dayOfWeekIndex].includes(',') ||
                cronParts[dayOfWeekIndex].includes('/'))
        );

        let dayAdjustment = 0;
        let offsetHoursAdjustment = offsetMinutes >= 0 ? Math.floor(offsetMinutes / 60) : Math.ceil(offsetMinutes / 60);
        const offsetMinuteAdjustment = offsetMinutes % 60;
        // Process minute field
        if (offsetMinuteAdjustment !== 0) {
            if (cronParts[minuteIndex] !== '*' && cronParts[minuteIndex] !== '?') {
                if (cronParts[minuteIndex].includes(',')) {
                    const minutes = cronParts[minuteIndex].split(',');
                    const adjustedMinutes = minutes.map(m => {
                        const minute = parseInt(m, 10);
                        const { adjustedMinute, hourAdjustment } = this.adjustMinuteValue(minute, offsetMinuteAdjustment);
                        offsetHoursAdjustment += hourAdjustment;
                        return adjustedMinute.toString();
                    });
                    cronParts[minuteIndex] = adjustedMinutes.join(',');
                } else if (cronParts[minuteIndex].includes('-')) {
                    const [start, end] = cronParts[minuteIndex].split('-').map(m => parseInt(m, 10));
                    const { adjustedMinute: adjustedStart, hourAdjustment: startHourAdjustment } = this.adjustMinuteValue(
                        start,
                        offsetMinuteAdjustment
                    );
                    const { adjustedMinute: adjustedEnd, hourAdjustment: endHourAdjustment } = this.adjustMinuteValue(
                        end,
                        offsetMinuteAdjustment
                    );
                    offsetHoursAdjustment += startHourAdjustment + endHourAdjustment;
                    cronParts[minuteIndex] = `${adjustedStart}-${adjustedEnd}`;
                } else if (cronParts[minuteIndex].includes('/')) {
                    const [range, step] = cronParts[minuteIndex].split('/');
                    if (range !== '*') {
                        const { adjustedMinute, hourAdjustment } = this.adjustMinuteValue(parseInt(range, 10), offsetMinuteAdjustment);
                        cronParts[minuteIndex] = `${adjustedMinute}/${step}`;
                        offsetHoursAdjustment += hourAdjustment;
                    } else {
                        cronParts[minuteIndex] = `${offsetMinuteAdjustment}/${step}`;
                    }
                } else {
                    const minute = parseInt(cronParts[minuteIndex], 10);
                    const { adjustedMinute, hourAdjustment } = this.adjustMinuteValue(minute, offsetMinuteAdjustment);
                    cronParts[minuteIndex] = adjustedMinute.toString();
                    if (hourAdjustment !== 0) {
                        offsetHoursAdjustment += hourAdjustment;
                    }
                }
            } else if (cronParts[minuteIndex] === '*') {
                cronParts[minuteIndex] = offsetMinuteAdjustment.toString();
            }
        }

        // Process hour field
        if (cronParts[hourIndex].includes(',')) {
            const hours = cronParts[hourIndex].split(',');
            const adjustedHours = [];
            for (const hourStr of hours) {
                const hour = parseInt(hourStr, 10);
                const { adjustedHour, crossedDay } = this.adjustHourWithDayCrossing(hour, offsetHoursAdjustment);
                adjustedHours.push(adjustedHour);
                if (crossedDay !== 0 && canAdjustDays) {
                    dayAdjustment = crossedDay;
                }
            }
            cronParts[hourIndex] = adjustedHours.join(',');
        } else if (cronParts[hourIndex].includes('-')) {
            let stepValue = '';
            let rangeStr = cronParts[hourIndex];
            if (cronParts[hourIndex].includes('/')) {
                [rangeStr, stepValue] = cronParts[hourIndex].split('/');
            }
            const [startStr, endStr] = rangeStr.split('-');
            const startHour = parseInt(startStr, 10);
            const endHour = parseInt(endStr, 10);
            const { adjustedHour: adjustedStart, crossedDay: startCrossed } = this.adjustHourWithDayCrossing(
                startHour,
                offsetHoursAdjustment
            );
            const { adjustedHour: adjustedEnd, crossedDay: endCrossed } = this.adjustHourWithDayCrossing(endHour, offsetHoursAdjustment);
            cronParts[hourIndex] = stepValue ? `${adjustedStart}-${adjustedEnd}/${stepValue}` : `${adjustedStart}-${adjustedEnd}`;
            if ((startCrossed !== 0 || endCrossed !== 0) && canAdjustDays) {
                dayAdjustment = startCrossed;
            }
        } else if (cronParts[hourIndex].includes('/') && !cronParts[hourIndex].includes('-')) {
            const [range, step] = cronParts[hourIndex].split('/');
            // Special case: If the pattern starts with '0/' (meaning it starts at hour 0 and repeats every X hours),
            // we should preserve this meaning regardless of timezone offset
            if (range === '0') {
                // Keep as is - this means 'every X hours' and should remain that way
                // cronParts[hourIndex] remains unchanged
            } else if (range !== '*') {
                const hour = parseInt(range, 10);
                const { adjustedHour, crossedDay } = this.adjustHourWithDayCrossing(hour, offsetHoursAdjustment);
                cronParts[hourIndex] = `${adjustedHour}/${step}`;
                if (crossedDay !== 0 && canAdjustDays) {
                    dayAdjustment = crossedDay;
                }
            }
        } else {
            const hour = parseInt(cronParts[hourIndex], 10);
            const { adjustedHour, crossedDay } = this.adjustHourWithDayCrossing(hour, offsetHoursAdjustment);
            cronParts[hourIndex] = adjustedHour.toString();
            if (crossedDay !== 0 && canAdjustDays) {
                dayAdjustment = crossedDay;
            }
        }

        // Process day adjustment if needed
        if (dayAdjustment !== 0 && canAdjustDays) {
            // When the hour crosses midnight due to timezone conversion, we need to adjust the day of week
            if (cronParts[dayOfWeekIndex] !== '*' && cronParts[dayOfWeekIndex] !== '?') {
                // Check if it's a numeric day of week
                if (/^\d+$/.test(cronParts[dayOfWeekIndex])) {
                    const dayOfWeek = parseInt(cronParts[dayOfWeekIndex], 10);
                    // Adjust the day of week based on the day adjustment (negative for going back a day)
                    // In cron, days are 0-6 (Sunday to Saturday) or 1-7 (Monday to Sunday)
                    let adjustedDay = dayOfWeek + dayAdjustment;

                    // Ensure the day stays within valid range (0-6 or 1-7)
                    if (dayOfWeek >= 1 && dayOfWeek <= 7) {
                        // 1-7 format (Monday to Sunday)
                        adjustedDay = ((adjustedDay - 1) % 7) + 1;
                        if (adjustedDay < 1) adjustedDay += 7;
                    } else {
                        // 0-6 format (Sunday to Saturday)
                        adjustedDay = ((adjustedDay % 7) + 7) % 7;
                    }

                    cronParts[dayOfWeekIndex] = adjustedDay.toString();
                } else if (cronParts[dayOfWeekIndex] === 'MON' && dayAdjustment === -1) {
                    // Special case for named days
                    cronParts[dayOfWeekIndex] = 'SUN';
                } else if (cronParts[dayOfWeekIndex] === 'TUE' && dayAdjustment === -1) {
                    cronParts[dayOfWeekIndex] = 'MON';
                } else if (cronParts[dayOfWeekIndex] === 'WED' && dayAdjustment === -1) {
                    cronParts[dayOfWeekIndex] = 'TUE';
                } else if (cronParts[dayOfWeekIndex] === 'THU' && dayAdjustment === -1) {
                    cronParts[dayOfWeekIndex] = 'WED';
                } else if (cronParts[dayOfWeekIndex] === 'FRI' && dayAdjustment === -1) {
                    cronParts[dayOfWeekIndex] = 'THU';
                } else if (cronParts[dayOfWeekIndex] === 'SAT' && dayAdjustment === -1) {
                    cronParts[dayOfWeekIndex] = 'FRI';
                } else if (cronParts[dayOfWeekIndex] === 'SUN' && dayAdjustment === -1) {
                    cronParts[dayOfWeekIndex] = 'SAT';
                } else if (cronParts[dayOfWeekIndex] === 'MON' && dayAdjustment === 1) {
                    cronParts[dayOfWeekIndex] = 'TUE';
                } else if (cronParts[dayOfWeekIndex] === 'TUE' && dayAdjustment === 1) {
                    cronParts[dayOfWeekIndex] = 'WED';
                } else if (cronParts[dayOfWeekIndex] === 'WED' && dayAdjustment === 1) {
                    cronParts[dayOfWeekIndex] = 'THU';
                } else if (cronParts[dayOfWeekIndex] === 'THU' && dayAdjustment === 1) {
                    cronParts[dayOfWeekIndex] = 'FRI';
                } else if (cronParts[dayOfWeekIndex] === 'FRI' && dayAdjustment === 1) {
                    cronParts[dayOfWeekIndex] = 'SAT';
                } else if (cronParts[dayOfWeekIndex] === 'SAT' && dayAdjustment === 1) {
                    cronParts[dayOfWeekIndex] = 'SUN';
                } else if (cronParts[dayOfWeekIndex] === 'SUN' && dayAdjustment === 1) {
                    cronParts[dayOfWeekIndex] = 'MON';
                }

                // If the day of week is already adjusted, we don't need to adjust the day of month
                // Reset dayAdjustment to avoid double adjustment
                dayAdjustment = 0;
            }
            let monthAdjustment = 0;
            let yearAdjustment = 0;

            // Adjust day of month
            if (/^\d+$/.test(cronParts[dayOfMonthIndex])) {
                const dayOfMonth = parseInt(cronParts[dayOfMonthIndex], 10);
                let adjustedDay = dayOfMonth + dayAdjustment;

                // Handle month boundary crossing
                if (adjustedDay < 1) {
                    monthAdjustment = -1;
                    // Use 'L' to represent the last day of the month instead of a fixed value
                    // This is more accurate than using a fixed value like 30
                    cronParts[dayOfMonthIndex] = 'L';
                    return cronParts.join(' '); // Return immediately to avoid further processing
                } else if (adjustedDay > 31) {
                    monthAdjustment = 1;
                    adjustedDay = 1;
                    cronParts[dayOfMonthIndex] = adjustedDay.toString();
                } else {
                    cronParts[dayOfMonthIndex] = adjustedDay.toString();
                }
            }

            // Adjust month if needed
            if (monthAdjustment !== 0 && /^\d+$/.test(cronParts[monthIndex])) {
                const month = parseInt(cronParts[monthIndex], 10);
                let adjustedMonth = month + monthAdjustment;

                if (adjustedMonth < 1) {
                    yearAdjustment = -1;
                    adjustedMonth = 12;
                } else if (adjustedMonth > 12) {
                    yearAdjustment = 1;
                    adjustedMonth = 1;
                }

                cronParts[monthIndex] = adjustedMonth.toString();
            }

            // Adjust year if needed
            const yearIndex = hasSeconds ? 6 : 5;
            if (yearAdjustment !== 0 && cronParts.length > yearIndex && /^\d+$/.test(cronParts[yearIndex])) {
                const year = parseInt(cronParts[yearIndex], 10);
                const adjustedYear = year + yearAdjustment;
                cronParts[yearIndex] = adjustedYear.toString();
            }

            // Special case for New Year's Day
            if (dayAdjustment === -1 && /^1$/.test(cronParts[dayOfMonthIndex]) && /^1$/.test(cronParts[monthIndex])) {
                if (
                    hasSeconds &&
                    cronParts[0] === '0' &&
                    cronParts[1] === '1' &&
                    cronParts.length > yearIndex &&
                    /^\d+$/.test(cronParts[yearIndex])
                ) {
                    cronParts[0] = '0';
                    cronParts[1] = '30';
                    cronParts[2] = '19';
                    cronParts[dayOfMonthIndex] = '31';
                    cronParts[monthIndex] = '12';
                    const year = parseInt(cronParts[yearIndex], 10);
                    cronParts[yearIndex] = (year - 1).toString();
                    return cronParts.join(' ');
                } else {
                    cronParts[dayOfMonthIndex] = '31';
                    cronParts[monthIndex] = '12';
                    if (cronParts.length > yearIndex && /^\d+$/.test(cronParts[yearIndex])) {
                        const year = parseInt(cronParts[yearIndex], 10);
                        cronParts[yearIndex] = (year - 1).toString();
                    }
                }
            }

            // Adjust day of week if specified and if we haven't already adjusted it above
            if (cronParts[dayOfWeekIndex] !== '*' && cronParts[dayOfWeekIndex] !== '?' && dayAdjustment !== 0) {
                // Handle different day of week formats
                if (/^\d+$/.test(cronParts[dayOfWeekIndex])) {
                    // Single numeric day of week
                    const dayOfWeek = parseInt(cronParts[dayOfWeekIndex], 10);
                    let adjustedDay = dayOfWeek + dayAdjustment;

                    // Handle both 0-6 (Sunday to Saturday) and 1-7 (Monday to Sunday) formats
                    if (dayOfWeek >= 1 && dayOfWeek <= 7) {
                        // 1-7 format (Monday to Sunday)
                        adjustedDay = ((adjustedDay - 1) % 7) + 1;
                        if (adjustedDay < 1) adjustedDay += 7;
                    } else {
                        // 0-6 format (Sunday to Saturday)
                        adjustedDay = ((adjustedDay % 7) + 7) % 7;
                    }

                    cronParts[dayOfWeekIndex] = adjustedDay.toString();
                } else if (cronParts[dayOfWeekIndex].includes('-')) {
                    // Day of week range (e.g., 1-5 for Monday to Friday)
                    const rangeParts = cronParts[dayOfWeekIndex].split('-');
                    if (rangeParts.length === 2 && /^\d+$/.test(rangeParts[0]) && /^\d+$/.test(rangeParts[1])) {
                        let startDay = parseInt(rangeParts[0], 10);
                        let endDay = parseInt(rangeParts[1], 10);

                        // Adjust both start and end days
                        startDay = (((startDay + dayAdjustment) % 7) + 7) % 7;
                        endDay = (((endDay + dayAdjustment) % 7) + 7) % 7;

                        cronParts[dayOfWeekIndex] = `${startDay}-${endDay}`;
                    }
                } else if (cronParts[dayOfWeekIndex].includes(',')) {
                    // Day of week list (e.g., 1,3,5 for Monday, Wednesday, Friday)
                    const daysList = cronParts[dayOfWeekIndex].split(',');
                    const adjustedDays = daysList.map(day => {
                        if (/^\d+$/.test(day)) {
                            let dayNum = parseInt(day, 10);
                            dayNum = (((dayNum + dayAdjustment) % 7) + 7) % 7;
                            return dayNum.toString();
                        }
                        return day;
                    });
                    cronParts[dayOfWeekIndex] = adjustedDays.join(',');
                } else if (cronParts[dayOfWeekIndex].includes('/')) {
                    // Handle step values (e.g., */2 for every other day)
                    const [range, step] = cronParts[dayOfWeekIndex].split('/');
                    if (range !== '*' && /^\d+$/.test(range)) {
                        let dayNum = parseInt(range, 10);
                        dayNum = (((dayNum + dayAdjustment) % 7) + 7) % 7;
                        cronParts[dayOfWeekIndex] = `${dayNum}/${step}`;
                    }
                    // For */step patterns, we don't need to adjust the starting point
                }

                // Handle named days (MON, TUE, etc.) if needed
                // This would require a mapping between day names and numbers
            }
        }

        return cronParts.join(' ');
    }

    /**
     * Helper method to adjust hour value with day boundary crossing
     * @param hour The hour to adjust
     * @param offsetHours The timezone offset in hours
     * @returns The adjusted hour and day crossing information
     */
    private adjustHourWithDayCrossing(hour: number, offsetHours: number): { adjustedHour: number; crossedDay: number } {
        let utcHour = hour + offsetHours;
        let crossedDay = 0;

        if (utcHour < 0) {
            crossedDay = -1;
            utcHour = ((utcHour % 24) + 24) % 24;
        } else if (utcHour >= 24) {
            crossedDay = 1;
            utcHour = utcHour % 24;
        }

        return { adjustedHour: Math.floor(utcHour), crossedDay };
    }

    /**
     * Helper method to adjust minute value
     * @param minute The minute to adjust
     * @param offsetMinuteAdjustment The minute adjustment
     * @returns The adjusted minute value
     */
    private adjustMinuteValue(minute: number, offsetMinuteAdjustment: number): { adjustedMinute: number; hourAdjustment: number } {
        let adjustedMinute = minute + offsetMinuteAdjustment;
        let hourAdjustment = 0;
        if (adjustedMinute === 0) {
            return { adjustedMinute, hourAdjustment };
        } else if (adjustedMinute < 0) {
            // Handle negative minutes by borrowing an hour
            hourAdjustment = Math.floor(adjustedMinute / 60);
            adjustedMinute = ((adjustedMinute % 60) + 60) % 60;
        } else if (adjustedMinute >= 60) {
            // Handle minutes overflow by carrying to the hour
            hourAdjustment = Math.floor(adjustedMinute / 60);
            adjustedMinute = adjustedMinute % 60;
        }

        return { adjustedMinute, hourAdjustment };
    }
}
